import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from './firebase';

/** Fallback content type for images whose File carries no type. */
const DEFAULT_IMAGE_CONTENT_TYPE = 'image/jpeg';

/**
 * Pathname shape of a Firebase Storage download URL: `/v0/b/{bucket}/o/{path}`,
 * where `{path}` is percent-encoded. Matched on shape rather than hostname
 * because the emulator serves these from `127.0.0.1:9199` — a check for
 * `firebasestorage.googleapis.com` would silently disable path recovery in
 * local QA. It also excludes signed GCS URLs (`/{bucket}/{path}`), which is how
 * AI reference images are served.
 */
const DOWNLOAD_URL_PATHNAME = /^\/v0\/b\/[^/]+\/o\/(.+)$/;

/** Root of the per-user namespace in Storage. */
export function userStorageRoot(userId: string): string {
  return `users/${userId}/`;
}

/** File extension the add-plant flow uploads under (PhotoCapture emits JPEG). */
const PLANT_UPLOAD_EXTENSION = 'jpg';

/**
 * Path for a photo uploaded by the add-plant flow. Named `temp-` because it is
 * taken before the plant document exists, so there is no plant ID to file it
 * under; the resulting path is stored on the plant as `photoStoragePath` so
 * deletion can find it later.
 */
export function plantUploadStoragePath(userId: string): string {
  return `${userStorageRoot(userId)}plants/temp-${Date.now()}.${PLANT_UPLOAD_EXTENSION}`;
}

/**
 * Upload an image to Firebase Storage and return its download URL.
 *
 * The single place this app performs an image upload. `AddPlant` still has its
 * own inline copy of this sequence (see `uploadAndIdentify`); new upload paths
 * should call this instead of adding a third copy.
 */
export async function uploadImage(path: string, file: File): Promise<string> {
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file, {
    contentType: file.type || DEFAULT_IMAGE_CONTENT_TYPE,
  });
  return getDownloadURL(storageRef);
}

/**
 * Recover the Storage object path from a Firebase download URL.
 *
 * Only for records written before their upload path was stored — a photo that
 * knows its own path (`PlantPhoto.storagePath`, `Plant.photoStoragePath`) must
 * use that instead of guessing from a URL.
 *
 * Returns null for anything that is not a Firebase Storage download URL:
 * `plant.photoURL` is just as likely to be an Unsplash or Wikimedia reference
 * photo, a signed URL for a shared AI-generated image, or the local
 * `/placeholder-plant.svg`.
 */
export function storagePathFromDownloadURL(url: string | null | undefined): string | null {
  if (!url) return null;

  try {
    const encodedPath = new URL(url).pathname.match(DOWNLOAD_URL_PATHNAME)?.[1];
    return encodedPath ? decodeURIComponent(encodedPath) : null;
  } catch {
    // Relative paths (the placeholder SVG) and malformed URLs land here.
    return null;
  }
}

/**
 * Narrow a Storage path to one this user owns, or null.
 *
 * The gate on every delete: Storage holds shared objects too — AI-generated
 * reference images live at `generated-reference/{slug}.jpg` and are cached for
 * every user, so deleting one because a single plant pointed at it would break
 * that species' reference photo for everyone.
 */
export function ownedStoragePath(
  userId: string,
  path: string | null | undefined
): string | null {
  return path?.startsWith(userStorageRoot(userId)) ? path : null;
}
